import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, TrendingUp, Receipt, Bell, RotateCcw, Gift } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import BalanceCard from "../components/BalanceCard";
import QuickActions from "../components/QuickActions";
import TransactionItem from "../components/TransactionItem";
import { SkeletonCard, SkeletonRow, EmptyState } from "../components/Skeleton";
import { Button } from "../components/ui/button";
import { formatIDR } from "../lib/api";
import { buildLoyaltySummary, formatLoyaltyRate } from "../lib/loyalty";

export default function DashboardPage() {
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();
    const [txs, setTxs] = useState(null);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        const [transactionsResult, statsResult] = await Promise.allSettled([
            api.get("/transactions?take=5"),
            api.get("/users/stats"),
        ]);

        if (transactionsResult.status === "fulfilled") {
            const data = transactionsResult.value.data;
            setTxs(Array.isArray(data) ? data : data?.items ?? []);
        } else {
            setTxs([]);
        }

        if (statsResult.status === "fulfilled") {
            setStats(statsResult.value.data);
        } else {
            setStats({ total_today_tx: 0, profit_today: 0, total_week_tx: 0, profit_week: 0, by_type: {}, daily: {} });
        }

        refreshUser();
        if (transactionsResult.status === "rejected" && statsResult.status === "rejected") {
            toast.error("Gagal memuat data dashboard");
        }
    };

    const repeatLast = async () => {
        if (!txs || txs.length === 0) return;
        const last = txs[0];
        const productLabel = last.product?.name || last.product_name || last.type;
        const target = last.targetNumber || last.customer_number || "";
        toast.info(`Mengulangi transaksi: ${productLabel}`);
        const service = { PULSA: "pulsa", DATA: "data", PLN: /tagihan|postpaid|pascabayar/i.test(productLabel) ? "pln_bill" : "pln_token", EWALLET: "ewallet", BPJS: "bpjs", PDAM: "pdam", OTHER: "other" }[String(last.type).toUpperCase()] || "other";
        navigate(`/transaksi/${service}?prefill=${encodeURIComponent(target)}`);
    };

    const loyalty = buildLoyaltySummary(stats, user);

    return (
        <div className="space-y-5">
            <div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest font-medium">Selamat datang</div>
                <h1 className="font-heading font-bold text-xl tracking-tight">{user?.owner_name} 👋</h1>
            </div>

            <BalanceCard user={user} />

            <button
                type="button"
                onClick={() => navigate("/bonus-warung")}
                className="w-full text-left rounded-2xl border border-brand-green/25 bg-brand-green/5 p-4 transition hover:border-brand-green/50 hover:bg-brand-green/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green"
                data-testid="bonus-warung-summary"
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Gift className="h-5 w-5 text-brand-green" />
                        <div>
                            <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">Bonus Warung</div>
                            <div className="font-heading font-bold mt-1">{loyalty.currentStatus.icon} {loyalty.currentStatus.name}</div>
                        </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-brand-green mt-1" />
                </div>
                {user?.role === "customer" ? (
                    <p className="text-xs text-muted-foreground mt-3">Upgrade ke Keluarga P4Yu untuk mengikuti program loyalitas.</p>
                ) : (
                    <div className="mt-3 flex items-center justify-between gap-3 text-xs"><span className="text-muted-foreground">Omzet bulan ini {formatIDR(loyalty.monthlyTurnover)}</span><span className="font-semibold text-brand-green">Bonus {formatLoyaltyRate(loyalty.bonusRate)}</span></div>
                )}
            </button>

            {/* Quick Actions */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-heading font-semibold text-base">Transaksi Cepat</h2>
                    {txs && txs.length > 0 && (
                        <button
                            data-testid="repeat-last-btn"
                            onClick={repeatLast}
                            className="flex items-center gap-1 text-xs font-semibold text-brand-green"
                        >
                            <RotateCcw className="h-3 w-3" /> Ulangi terakhir
                        </button>
                    )}
                </div>
                <div className="rounded-2xl bg-card border border-border p-4">
                    <QuickActions />
                </div>
            </div>

            {/* Stats */}
            <div>
                <h2 className="font-heading font-semibold text-base mb-3">Statistik Hari Ini</h2>
                <div className="grid grid-cols-2 gap-3">
                    {stats === null ? (
                        <>
                            <SkeletonCard />
                            <SkeletonCard />
                        </>
                    ) : (
                        <>
                            <motion.div
                                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                                className="rounded-2xl bg-card border border-border p-4"
                                data-testid="stat-tx-today"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="text-xs text-muted-foreground">Transaksi</div>
                                    <Receipt className="h-4 w-4 text-brand-green" />
                                </div>
                                <div className="font-heading font-bold text-2xl mt-1">{stats.total_today_tx}</div>
                                <div className="text-[10px] text-muted-foreground mt-1">Hari ini</div>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                                className="rounded-2xl bg-card border border-border p-4"
                                data-testid="stat-profit-today"
                            >
                                <div className="flex items-center justify-between">
                                    <div className="text-xs text-muted-foreground">Profit</div>
                                    <TrendingUp className="h-4 w-4 text-brand-green" />
                                </div>
                                <div className="font-heading font-bold text-2xl mt-1 text-brand-green">{formatIDR(stats.profit_today)}</div>
                                <div className="text-[10px] text-muted-foreground mt-1">Hari ini</div>
                            </motion.div>
                        </>
                    )}
                </div>
                {stats && stats.total_week_tx > 0 && (
                    <div className="mt-3 rounded-2xl bg-gradient-to-r from-brand-green/10 to-brand-deep/5 border border-brand-green/20 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-xs text-muted-foreground">Total profit 7 hari</div>
                                <div className="font-heading font-bold text-xl text-brand-green mt-1">{formatIDR(stats.profit_week)}</div>
                            </div>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate("/statistik")}
                                className="text-brand-green"
                                data-testid="goto-stats-btn"
                            >
                                Detail <ArrowRight className="h-4 w-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Recent transactions */}
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-heading font-semibold text-base">Transaksi Terakhir</h2>
                    <button
                        onClick={() => navigate("/riwayat")}
                        className="text-xs font-semibold text-brand-green"
                        data-testid="see-all-tx-btn"
                    >
                        Lihat semua →
                    </button>
                </div>
                <div className="space-y-2">
                    {txs === null ? (
                        <>
                            <SkeletonRow />
                            <SkeletonRow />
                            <SkeletonRow />
                        </>
                    ) : txs.length === 0 ? (
                        <EmptyState
                            title="Belum ada transaksi"
                            description="Mulai transaksi pertama Anda. Pilih produk dari menu di atas."
                            icon={<Bell className="h-7 w-7 text-muted-foreground" />}
                            action={
                                <Button
                                    data-testid="empty-start-tx-btn"
                                    onClick={() => navigate("/transaksi/pulsa")}
                                    className="bg-brand-green hover:bg-brand-greenDark text-white rounded-xl"
                                >
                                    Mulai Transaksi
                                </Button>
                            }
                        />
                    ) : (
                        txs.map((tx, i) => (
                            <TransactionItem
                                key={tx.id}
                                tx={tx}
                                index={i}
                                onClick={() => navigate(`/riwayat/${tx.id}`)}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
